import { z } from 'zod';

import { apiDelete, apiGet, apiPost, apiPut } from '../client';
import {
  AgentConversationSchema,
  AgentRuntimeSchema,
  ConversationListResponseSchema,
  ConversationThreadSchema,
  ReceptionistResponseSchema,
  SaveReceptionistResponseSchema,
  WhatsAppPairingSchema,
  type AgentConversation,
  type AgentRuntime,
  type ConversationListResponse,
  type ConversationStatus,
  type ConversationThread,
  type ReceptionistEdit,
  type ReceptionistSettings,
  type SaveReceptionistResponse,
  type WhatsAppPairing,
} from '../agentRuntimeSchemas';

/** Dale Agents (Ultra): the receptionist, its runtime and the Bandeja. No screens here, only the wire. */

const BASE = '/v1/business/agents';
const CONVERSATIONS = `${BASE}/conversations`;

const data = <T extends z.ZodType>(schema: T) => z.object({ data: schema });

export async function getReceptionist(): Promise<ReceptionistSettings> {
  return (await apiGet(`${BASE}/receptionist`, ReceptionistResponseSchema)).data;
}

/** Saves the whole settings and pushes them to the runtime; `synced` says whether the push landed. */
export function saveReceptionist(edit: ReceptionistEdit): Promise<SaveReceptionistResponse> {
  return apiPut(`${BASE}/receptionist`, SaveReceptionistResponseSchema, edit);
}

export async function pairWhatsApp(): Promise<WhatsAppPairing> {
  return (await apiPost(`${BASE}/whatsapp/pair`, data(WhatsAppPairingSchema))).data;
}

export async function unlinkWhatsApp(idempotencyKey: string): Promise<void> {
  await apiDelete(`${BASE}/whatsapp`, data(z.object({ unlinked: z.literal(true) })), idempotencyKey);
}

/** Live runtime status plus this month's usage against the cap. */
export async function getAgentRuntime(): Promise<AgentRuntime> {
  return (await apiGet(`${BASE}/runtime`, data(AgentRuntimeSchema))).data;
}

export async function pauseReceptionist(): Promise<ReceptionistSettings> {
  return (await apiPost(`${BASE}/pause`, ReceptionistResponseSchema)).data;
}

/** 409 `conflict` while the month's cap is reached. */
export async function resumeReceptionist(): Promise<ReceptionistSettings> {
  return (await apiPost(`${BASE}/resume`, ReceptionistResponseSchema)).data;
}

export type ConversationFilter = { status?: ConversationStatus; cursor?: string; limit?: number };

export function listConversations(filter: ConversationFilter = {}): Promise<ConversationListResponse> {
  const query = new URLSearchParams();
  if (filter.status) query.set('status', filter.status);
  if (filter.cursor) query.set('cursor', filter.cursor);
  if (filter.limit !== undefined) query.set('limit', String(filter.limit));
  const suffix = query.toString();
  return apiGet(`${CONVERSATIONS}${suffix ? `?${suffix}` : ''}`, ConversationListResponseSchema);
}

export async function getConversation(id: string): Promise<ConversationThread> {
  return (await apiGet(`${CONVERSATIONS}/${encodeURIComponent(id)}`, data(ConversationThreadSchema))).data;
}

export async function takeOverConversation(id: string): Promise<AgentConversation> {
  return (await apiPost(`${CONVERSATIONS}/${encodeURIComponent(id)}/takeover`, data(AgentConversationSchema))).data;
}

export async function releaseConversation(id: string): Promise<AgentConversation> {
  return (await apiPost(`${CONVERSATIONS}/${encodeURIComponent(id)}/release`, data(AgentConversationSchema))).data;
}
